import { useDisplayStore } from '../store';
import { THEMES, type ScoreboardConfig } from '../channels/scoreboard/themes';
import { ZoneRenderer } from './ZoneRenderer';
import { resolveLayoutTheme, WidgetContext } from './context';
import { useResolvedTemplate } from './useResolvedTemplate';
import { AnimationHost } from '../effects/AnimationHost';
import { BESPOKE_DESIGNS, isBespoke } from './bespoke/registry';

// Live, full-viewport scoreboard rendered through the widget engine.
// Used by the SCOREBOARD channel when the display's config opts into a
// zone template (config.layoutTemplate — a built-in key or saved-layout
// UUID). Falls back to sample data when no game is linked yet.
export function LiveBoard({ config }: { config: ScoreboardConfig | null }) {
  const snapshot = useDisplayStore((s) => s.liveSnapshot);
  const template = useResolvedTemplate(config?.layoutTemplate);
  const baseTheme = THEMES[config?.theme ?? 'classic'] ?? THEMES.classic;
  const { theme, mode, font, frame } = resolveLayoutTheme(
    baseTheme,
    config?.digitStyle ?? 'segment',
    template.theme,
  );
  const live = !!(config?.assignedGameId && snapshot);
  // themeKey must match the palette resolveLayoutTheme actually selected:
  // template.theme?.preset wins over config?.theme, mirroring resolveLayoutTheme's
  // own precedence (lt?.preset → base). This keeps AnimationHost's deriveEffectTheme
  // reading per-skin overrides from the same skin as the resolved theme.bg/ac/etc.
  const themeKey = template.theme?.preset ?? config?.theme ?? 'classic';

  const data = {
    snapshot: live ? snapshot : null,
    state: live ? snapshot!.state : null,
    theme,
    mode,
    font,
    frame,
    isSample: !live,
  };

  return (
    <div style={{ position: 'relative', width: '100vw', height: '100vh', overflow: 'hidden' }}>
      {isBespoke(template.id) ? (
        <WidgetContext.Provider value={data}>
          {(() => { const Design = BESPOKE_DESIGNS[template.id]; return <Design />; })()}
        </WidgetContext.Provider>
      ) : (
        <ZoneRenderer template={template} data={data} />
      )}
      <AnimationHost enabled={config?.effects} theme={theme} themeKey={themeKey} />
    </div>
  );
}
